import { useMemo, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { FormModal } from '@/components/ui/form-modal'
import { FormSection } from '@/components/ui/form-section'
import {
  MultiSelectFieldControl,
  SelectFieldControl,
  SwitchFieldControl,
  TextAreaFieldControl,
} from '@/components/ui/form-fields'
import { PAGE_LIMIT_MAX } from '@/lib/api/common'

import { useCountActions, useInventoryLocations, useStockItems } from '../hooks'
import type { CreateCountBody, StockCount } from '../types'
import { countScopeLabels, inOutletOrder, isCounted, locationLabel } from './inventory-format'

const CANDIDATE_QUERY = { limit: PAGE_LIMIT_MAX, offset: 0 }

const schema = z
  .object({
    locationId: z.string().min(1, 'Choose a location.'),
    scope: z.enum(['full', 'partial']),
    itemIds: z.array(z.string()),
    blind: z.boolean(),
    note: z.string().max(2000).optional(),
  })
  .superRefine((values, context) => {
    if (values.scope === 'partial' && values.itemIds.length === 0) {
      context.addIssue({ code: 'custom', path: ['itemIds'], message: 'Pick the items to count.' })
    }
  })

type FormValues = z.infer<typeof schema>

interface CountFormDialogProps {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  /** The new count, so the list can open its sheet straight away. */
  onCreated: (count: StockCount) => void
}

/**
 * Starts a stock count at one location: every counted item, or the ones picked.
 * Nothing is counted yet — the sheet does that, line by line.
 */
export function CountFormDialog({ isOpen, onOpenChange, onCreated }: CountFormDialogProps) {
  const { create } = useCountActions()
  const [clientDocId] = useState(() => crypto.randomUUID())
  const [idempotencyKey] = useState(() => crypto.randomUUID())

  const { control, handleSubmit } = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: 'onTouched',
    defaultValues: { locationId: '', scope: 'full', itemIds: [], blind: true, note: '' },
  })
  const scope = useWatch({ control, name: 'scope' })

  const { locations } = useInventoryLocations()
  const locationOptions = useMemo(
    () =>
      inOutletOrder(locations).map((location) => ({
        value: location.id,
        label: locationLabel(location),
      })),
    [locations],
  )

  const { items: candidates } = useStockItems(CANDIDATE_QUERY)
  const itemOptions = useMemo(
    () =>
      candidates
        .filter(isCounted)
        .map((item) => ({ value: item.id, label: `${item.name} (${item.unitOfMeasure})` })),
    [candidates],
  )

  const submit = handleSubmit(async (form) => {
    const body: CreateCountBody = {
      locationId: form.locationId,
      scope: form.scope,
      blind: form.blind,
      clientDocId,
      ...(form.scope === 'partial' && { itemIds: form.itemIds }),
      ...(form.note?.trim() && { note: form.note.trim() }),
    }

    onCreated(await create.mutateAsync({ body, idempotencyKey }))
  })

  return (
    <FormModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="New stock count"
      description="Count what is on the shelf. Stock only changes when you submit, and sales made during the count are kept."
      submitLabel="Start count"
      onSubmit={submit}
      isBusy={create.isPending}
      error={create.error}
      errorFallback="Could not start this count."
      dialogClassName="max-w-2xl"
    >
      <FormSection
        title="Where and what"
        description="Pick the location, then count everything or just a few items."
      >
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectFieldControl
              control={control}
              name="locationId"
              label="Location"
              placeholder="Choose a location"
              isRequired
              options={locationOptions}
            />
            <SelectFieldControl
              control={control}
              name="scope"
              label="What to count"
              isRequired
              options={(['full', 'partial'] as const).map((value) => ({
                value,
                label: countScopeLabels[value],
              }))}
            />
          </div>

          {scope === 'partial' && (
            <MultiSelectFieldControl
              control={control}
              name="itemIds"
              label="Items"
              placeholder="Pick items…"
              description="Only items whose stock is tracked are listed."
              isRequired
              options={itemOptions}
            />
          )}
        </div>
      </FormSection>

      <FormSection title="Options">
        <div className="flex flex-col gap-5">
          <SwitchFieldControl
            control={control}
            name="blind"
            label="Blind count"
            description="Hide the expected quantity, so the counter writes down only what they see."
          />
          <TextAreaFieldControl
            control={control}
            name="note"
            label="Note"
            placeholder="Optional, e.g. month-end count, back store room"
            rows={3}
          />
        </div>
      </FormSection>
    </FormModal>
  )
}
